// generated by the plop

import { Injectable } from '@angular/core';
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import { cloneDeep } from 'lodash';
import { of, switchMap } from 'rxjs';
import {
  CreatedStandByWS,
  CreateStandTemplate,
  DeleteStandTemplate,
  GetStandTemplateList,
  IssuedStand,
  IssuedStandFail,
  IssuedStandSuccess,
  ReadyToPickUpStand,
  ReadyToPickUpStandFail,
  ReadyToPickUpStandSuccess,
  StandModel,
  STANDS_LOCAL_STORAGE_KEY,
  StandService,
  UpdatedStandByWS,
  UpdateStandTemplate,
  UpdateStandTemplateFail,
  UpdateStandTemplateSuccess
} from 'shared';
import { PROGRESS_STATUSES } from 'symbols';
import {
  CreateStand,
  CreateStandFail,
  CreateStandSuccess,
  DeleteLocalStand,
  GetStandById,
  GetStandByIdFail,
  GetStandByIdSuccess,
  SetCurrentStandId,
  UpdateLocalStand,
  UpdateStand,
  UpdateStandFail,
  UpdateStandSuccess
} from './stand-manage.actions';
import { STAND_MANAGE_STATE_DEFAULT, StandManageStateModel } from './stand-manage.model';

@State({ name: 'StandManage', defaults: STAND_MANAGE_STATE_DEFAULT })
@Injectable()
export class StandManageState implements NgxsOnInit {

  constructor(private standService: StandService) {
  }


  @Selector()
  static standTemplateList(state: StandManageStateModel): StandManageStateModel['templateList'] {
    return state.templateList;
  }


  @Selector()
  static realVersionStand(state: StandManageStateModel): StandManageStateModel['realVersionStands'] {
    return state.realVersionStands;
  }

  @Selector()
  static currentStand(state: StandManageStateModel): StandModel {
    return state.stands?.[state.currentStandId];
  }

  @Selector()
  static loadStandStatus(state: StandManageStateModel): PROGRESS_STATUSES {
    return state.loadStandStatus;
  }


  ngxsOnInit(ctx: StateContext<StandManageStateModel>) {
    const localStorageStands = localStorage.getItem(STANDS_LOCAL_STORAGE_KEY);
    if (localStorageStands) {
      const stands = JSON.parse(localStorageStands);
      ctx.patchState({
        stands
      });
    }
    ctx.dispatch(new GetStandTemplateList());
  }


  @Action(GetStandTemplateList)
  getStandTemplateList(ctx: StateContext<StandManageStateModel>): void {
    ctx.patchState({
      loadTemplateListStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.standService.getStandTemplateList().subscribe({
      next: templateList => ctx.patchState({ templateList, loadTemplateListStatus: PROGRESS_STATUSES.SUCCESS }),
      error: err => ctx.patchState({ loadTemplateListStatus: PROGRESS_STATUSES.FAIL })
    });
  }

  @Action(CreateStandTemplate)
  createStandTemplate(ctx: StateContext<StandManageStateModel>, { template, name }: CreateStandTemplate): void {
    ctx.patchState({
      loadTemplateListStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.standService.createTemplate(name, template).subscribe({
      next: () => ctx.dispatch([new GetStandTemplateList(), new UpdateStandTemplateSuccess()]),
      error: err => {
        ctx.patchState({ loadTemplateListStatus: PROGRESS_STATUSES.FAIL });
        ctx.dispatch(new UpdateStandTemplateFail(err));
      }
    });
  }

  @Action(UpdateStandTemplate)
  updateStandTemplate(ctx: StateContext<StandManageStateModel>, { template, id }: UpdateStandTemplate): void {
    ctx.patchState({
      loadTemplateListStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.standService.updateTemplate(id, {
      html: template.html,
      project: template.project,
      formatId: template.formatId,
      css: template.css
    }, template.name).subscribe({
      next: () => ctx.dispatch([new GetStandTemplateList(), new UpdateStandTemplateSuccess()]),
      error: err => {
        ctx.patchState({ loadTemplateListStatus: PROGRESS_STATUSES.FAIL });
        ctx.dispatch(new UpdateStandTemplateFail(err));
      }
    });
  }

  @Action(DeleteStandTemplate)
  deleteStandTemplate(ctx: StateContext<StandManageStateModel>, { id }: DeleteStandTemplate): void {
    ctx.patchState({
      loadTemplateListStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.standService.deleteTemplate(id).subscribe({
      next: () => ctx.dispatch([new GetStandTemplateList(), new UpdateStandTemplateSuccess()]),
      error: err => {
        ctx.patchState({ loadTemplateListStatus: PROGRESS_STATUSES.FAIL });
        ctx.dispatch(new UpdateStandTemplateFail(err));
      }
    });
  }


  @Action(GetStandById)
  getStandById(ctx: StateContext<StandManageStateModel>, { id }: GetStandById): void {
    ctx.patchState({
      loadStandStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    if (ctx.getState().stands?.[id]?.id) {
      this.standService.getStandById(id).subscribe({
        next: stand => {
          if (stand) {
            ctx.dispatch(new UpdatedStandByWS(stand));
          } else {
            ctx.patchState({
              loadStandStatus: PROGRESS_STATUSES.SUCCESS
            });
          }
          ctx.patchState({
            currentStandId: id
          });
        },
        error: err => ctx.dispatch(new GetStandByIdFail(err))
      });
    } else {
      this.standService.getStandById(id).subscribe({
        next: stand => ctx.dispatch(new GetStandByIdSuccess(stand)),
        error: err => ctx.dispatch(new GetStandByIdFail(err))
      });
    }
  }

  @Action(GetStandByIdSuccess)
  getStandByIdSuccess(ctx: StateContext<StandManageStateModel>, { stand }: GetStandByIdSuccess): void {
    const stands = cloneDeep(ctx.getState().stands);
    stands[stand.id] = stand;
    ctx.patchState({
      stands,
      currentStandId: stand.id,
      loadStandStatus: PROGRESS_STATUSES.SUCCESS
    });
  }

  @Action(GetStandByIdFail)
  getStandByIdFail(ctx: StateContext<StandManageStateModel>, { error }: GetStandByIdFail): void {
    ctx.patchState({
      loadStandStatus: PROGRESS_STATUSES.FAIL
    });
  }

  @Action(SetCurrentStandId)
  setCurrentStandId(ctx: StateContext<StandManageStateModel>, { id }: SetCurrentStandId): void {
    ctx.patchState({
      currentStandId: id
    });
  }

  @Action(UpdateLocalStand)
  updateLocalStand(ctx: StateContext<StandManageStateModel>, { stand }: UpdateLocalStand): void {
    const stands = cloneDeep(ctx.getState().stands);
    stands[stand.id] = { ...stands[stand.id], ...stand };
    localStorage.setItem(STANDS_LOCAL_STORAGE_KEY, JSON.stringify(stands));
    ctx.patchState({
      stands
    });
  }

  @Action(DeleteLocalStand)
  deleteLocalStand(ctx: StateContext<StandManageStateModel>, { id }: DeleteLocalStand): void {
    const stands = cloneDeep(ctx.getState().stands);
    const realVersionStands = cloneDeep(ctx.getState().realVersionStands);

    delete stands[id];
    delete realVersionStands[id];
    ctx.patchState({
      stands,
      realVersionStands
    });
  }


  @Action(UpdateStand)
  updateStand(ctx: StateContext<StandManageStateModel>, { id, stand, force }: UpdateStand): void {
    ctx.patchState({
      updateStandStatus: PROGRESS_STATUSES.IN_PROGRESS
    });

    this.standService.updateStand(id, stand, force).subscribe({
      next: updatedStand => ctx.dispatch(new UpdateStandSuccess(updatedStand)),
      error: err => ctx.dispatch(new UpdateStandFail(err))
    });
  }

  @Action(UpdateStandSuccess)
  updateStandSuccess(ctx: StateContext<StandManageStateModel>, { stand }: UpdateStandSuccess): void {
    const stands = cloneDeep(ctx.getState().stands);
    ctx.patchState({
      updateStandStatus: PROGRESS_STATUSES.SUCCESS,
      stands: { ...stands, [stand.id]: { ...stands[stand.id], version: stand.version, debtId: stand.debtId, debt: stand.debt } }
    });
  }

  @Action([UpdatedStandByWS])
  updateStandByWS(ctx: StateContext<StandManageStateModel>, { stand }: UpdatedStandByWS): void {
    const realVersionStands = cloneDeep(ctx.getState().realVersionStands);
    const stands = cloneDeep(ctx.getState().stands);
    const standExist = stands[stand.id];
    if (standExist) {
      ctx.patchState({
        loadStandStatus: PROGRESS_STATUSES.SUCCESS,
        stands: {
          ...stands,
          [stand.id]: {
            ...stands[stand.id],
            updater: stand.updater,
            debt: stand.debt,
            debtId: stand.debtId
          }
        }
      });
    }
    ctx.patchState({
      loadStandStatus: PROGRESS_STATUSES.SUCCESS,
      realVersionStands: { ...realVersionStands, [stand.id]: stand }
    });
  }

  @Action(CreatedStandByWS)
  createdStandByWS(ctx: StateContext<StandManageStateModel>, { stand }: CreatedStandByWS): void {
    const stands = cloneDeep(ctx.getState().stands);
    ctx.patchState({
      stands: {
        ...stands,
        [stand.id]: {
          ...stands[stand.id],
          isChanged: false,
          isNew: false,
          updater: stand.updater,
          client: stand.client
        }
      }
    });
  }


  @Action(CreateStand)
  createStand(ctx: StateContext<StandManageStateModel>, { stand }: CreateStand): void {
    ctx.patchState({
      updateStandStatus: PROGRESS_STATUSES.IN_PROGRESS
    });

    this.standService.createStand(stand).subscribe({
      next: createdStand => ctx.dispatch(new CreateStandSuccess(createdStand)),
      error: err => ctx.dispatch(new CreateStandFail(err))
    });
  }

  @Action(CreateStandSuccess)
  createStandSuccess(ctx: StateContext<StandManageStateModel>, { stand }: CreateStandSuccess): void {
    const stands = cloneDeep(ctx.getState().stands);
    ctx.patchState({
      updateStandStatus: PROGRESS_STATUSES.SUCCESS,
      stands: { ...stands, [stand.id]: { ...stands[stand.id], debtId: stand.debtId, debt: stand.debt } }
    });
  }

  @Action(CreateStandFail)
  createStandFail(ctx: StateContext<StandManageStateModel>, { error }: CreateStandFail): void {
    ctx.patchState({
      updateStandStatus: PROGRESS_STATUSES.FAIL
    });
  }

  @Action(IssuedStand)
  acceptStand(ctx: StateContext<StandManageStateModel>, { standId }: IssuedStand): void {
    this.standService.acceptStand(standId).subscribe({
      next: updatedStand => ctx.dispatch(new IssuedStandSuccess(updatedStand)),
      error: err => ctx.dispatch(new IssuedStandFail(err))
    });
  }

  @Action(IssuedStandSuccess)
  issuedStandSuccess(ctx: StateContext<StandManageStateModel>, { stand }: IssuedStandSuccess): void {
    const stands = cloneDeep(ctx.getState().stands);
    ctx.patchState({
      stands: { ...stands, [stand.id]: { ...stands[stand.id], ...stand } }
    });

  }

  @Action(IssuedStandFail)
  issuedStandFail(ctx: StateContext<StandManageStateModel>, { error }: IssuedStandFail): void {
    console.log(error);
  }

  @Action(ReadyToPickUpStand)
  readyToPickUpStand(ctx: StateContext<StandManageStateModel>, { setStatus, stand }: ReadyToPickUpStand): void {
    this.standService.updateStand(stand.id, stand, false)
      .pipe(switchMap(stand => {
        if (setStatus) {
          return this.standService.readyToPickUpStand(stand.id);
        } else {
          return of(stand);
        }
      }))
      .subscribe({
        next: res => ctx.dispatch(new ReadyToPickUpStandSuccess(res)),
        error: err => ctx.dispatch(new ReadyToPickUpStandFail(err))
      });
  }

  @Action(ReadyToPickUpStandSuccess)
  readyToPickUpStandSuccess(ctx: StateContext<StandManageStateModel>, { stand }: ReadyToPickUpStandSuccess): void {
    const stands = cloneDeep(ctx.getState().stands);
    ctx.patchState({
      stands: { ...stands, [stand.id]: { ...stands[stand.id], ...stand } }
    });

  }

  @Action(ReadyToPickUpStandFail)
  readyToPickUpStandFail(ctx: StateContext<StandManageStateModel>, { error }: ReadyToPickUpStandFail): void {
    console.log(error);
  }
}




